import _map from 'lodash/map';
import manualAssignments from '../core/gradebook/manual-assignments';
import { updateAssignmentException } from '../core/exceptions';
import { getCourseRoster } from '../core/lms';
import {
  batchSaveGrades,
  getGradebook,
  getGradebookInstructor,
  updateAlert,
} from './gradebook';
// import { getCourse } from '../courses'; // TODO: Delete/Replace in RNI-3156
import { APP_LOADING, REMOVE_ASSIGNMENT } from '../redux/constants';
import {
  DEFAULT_ASSIGNED_TO,
  GRADEBOOK_GRADING_TYPES_EXTRA_CREDIT,
  GRADEBOOK_GRADING_TYPES_GRADED,
} from '../utils/constants';
import isEmpty from 'lodash/isEmpty';

export const getGradeAsPercent = (grade, pointsPossible) => {
  const gradeInt = parseInt(grade, 10);
  const pointsPossibleInt = parseInt(pointsPossible, 10);

  let gradeAsPercent = null;
  if (pointsPossibleInt !== 0) {
    gradeAsPercent = gradeInt / pointsPossibleInt;
  }
  return gradeAsPercent;
};

export const saveManualAssignments = (form) => async (dispatch, getState) => {
  dispatch({
    type: APP_LOADING,
    payload: true,
  });

  const state = getState();
  const { courseId } = state.gradebook;
  const entryDate = form.entryDate;
  const pointsPossible = form.pointsPossible;

  const assignmentSelections = getAssignmentSelections(form);
  const isAssignedToEveryone = checkIsAssignedToEveryone(form);

  validateNonEmptyAssignment(assignmentSelections, isAssignedToEveryone);

  if (!isAssignedToEveryone) {
    form.entryDate = null;
    form.pointsPossible = null;
  }
  const assignmentResponse = await manualAssignments.save({ courseId, form });
  const manualAssignment = assignmentResponse.manualAssignment;
  const hasExceptionCondition = checkExceptionCondition(
    assignmentSelections,
    isAssignedToEveryone,
  );

  // Handle saving the exception
  if (hasExceptionCondition) {
    const { id, course_id } = manualAssignment;
    const exceptions = [
      {
        course_id,
        assignment_id: id,
        exception_type: 'assignment',
        user_ids: assignmentSelections.studentIds,
        group_ids: assignmentSelections.exceptionGroupIds,
        exception_value: {
          due_date: entryDate,
          assigned_points: pointsPossible,
        },
      },
    ];
    await updateAssignmentException({ courseId, exceptions }, id);
    dispatch({
      type: APP_LOADING,
      payload: false,
    });
  }

  // Handle any initial grade or extra credit grade changes
  const setInitialGrade =
    form.setInitialGrade && form.gradingType === GRADEBOOK_GRADING_TYPES_GRADED;
  const setExtraCreditScore =
    form.setExtraCreditScore &&
    form.gradingType === GRADEBOOK_GRADING_TYPES_EXTRA_CREDIT;
  if (setInitialGrade || setExtraCreditScore) {
    const roster = isAssignedToEveryone
      ? await getCourseRoster(courseId)
      : state.gradebook.roster;
    const studentIdsToUpdateGrades = getStudentIdsToBatchSaveGrades(
      assignmentSelections,
      isAssignedToEveryone,
      roster,
    );

    const grade = setInitialGrade ? form.initialGrade : form.extraCreditScore;
    const gradeAsPercent = getGradeAsPercent(grade, pointsPossible);

    await dispatch(
      batchSaveGrades({
        studentIds: studentIdsToUpdateGrades,
        grade: gradeAsPercent,
        courseId,
        assignmentId: manualAssignment.id,
      }),
    );
  }

  // Clean up and update the gradebook
  // TODO update the assignments without making an extra network call
  dispatch({
    type: REMOVE_ASSIGNMENT,
    payload: { assignment: manualAssignment },
  });
  const isGradebookV2Active = state.gradebook.isGradebookV2Active;
  if (isGradebookV2Active) {
    await dispatch(getGradebookInstructor(courseId));
  } else {
    await dispatch(getGradebook(courseId));
  }
  dispatch(
    updateAlert({
      show: true,
      type: 'success',
      dismissible: true,
      text: `The column and grades for "${manualAssignment.name}" has been added`,
    }),
  );
};

export const removeManualAssignment =
  (assignment) => async (dispatch, getState) => {
    const { courseId } = getState().gradebook;
    const failure = {
      show: true,
      type: 'warning',
      dismissible: true,
      text: 'Unable to delete column and grades.',
    };
    try {
      const { id: assignmentId, name } = assignment;
      const manualAssignmentResponse = await manualAssignments.remove({
        courseId,
        assignmentId,
      });
      if (manualAssignmentResponse.status === 200) {
        dispatch(
          updateAlert({
            show: true,
            type: 'success',
            dismissible: true,
            text: `Column and grades for "${name}" deleted`,
          }),
        );
        dispatch({ type: REMOVE_ASSIGNMENT, payload: { assignment } });
      } else {
        dispatch(updateAlert(failure));
      }
    } catch (_e) {
      dispatch(updateAlert(failure));
    }
  };

const getAssignmentSelections = (form) => {
  // The selectedStudents field has all selected students and/or groups
  return form.selectedStudents?.reduce(
    (acc, item) => {
      if (item.type === 'student') {
        // Student that was selected
        if (item.studentId) {
          acc['studentIds'].push(item.studentId);
        }
      } else {
        if (item.groupId) {
          // Exception group that was selected
          acc['exceptionGroupIds'].push(item.groupId);

          // The students within the exception group
          if (!isEmpty(item.studentIds)) {
            acc['exceptionGroupStudentIds'].push(...item.studentIds);
          }
        }
      }
      return acc;
    },
    { studentIds: [], exceptionGroupIds: [], exceptionGroupStudentIds: [] },
  );
};

const checkIsAssignedToEveryone = (form) => {
  const firstItem = form.selectedStudents[0]?.studentId;
  return firstItem === DEFAULT_ASSIGNED_TO;
};

const validateNonEmptyAssignment = (
  { studentIds, exceptionGroupIds },
  isAssignedToEveryone,
) => {
  if (
    studentIds.length < 1 &&
    exceptionGroupIds.length < 1 &&
    !isAssignedToEveryone
  ) {
    throw Error('Cannot save an assignment without assigned students');
  }
};

const checkExceptionCondition = (
  { studentIds, exceptionGroupIds },
  isAssignedToEveryone,
) => {
  if (isAssignedToEveryone) {
    return false;
  }
  return studentIds.length > 0 || exceptionGroupIds.length > 0;
};

const getStudentIdsToBatchSaveGrades = (
  { studentIds, exceptionGroupStudentIds },
  isAssignedToEveryone,
  roster,
) => {
  const specificStudentIdsToUpdate = [
    ...new Set([...studentIds, ...exceptionGroupStudentIds]),
  ];

  return isAssignedToEveryone
    ? _map(roster, ({ studentId }) => studentId)
    : specificStudentIdsToUpdate;
};
